import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { clampToZero } from 'shared/lib/number/number';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { ReportMetric } from 'shared/ui/ReportMetric';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import type { SpendInsightsSummaryDto } from 'entities/ReportSpendSummary';

interface SpendSummaryTotalSpendMetricProps {
    summary: SpendInsightsSummaryDto | null;
    primaryAmount: string | number | null;
    loading?: boolean;
    showSavingsDetail?: boolean;
}

const detailRowSx = {
    color: 'common.black',
    fontSize: '1.3rem',
    lineHeight: '1.6rem',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
} as const;

const detailValueSx = {
    fontVariantNumeric: 'tabular-nums' as const,
    fontWeight: 600,
} as const;

export const SpendSummaryTotalSpendMetric = memo(
    ({
        summary,
        primaryAmount,
        loading = false,
        showSavingsDetail = true,
    }: SpendSummaryTotalSpendMetricProps) => {
        const { t } = useTranslation();
        const savingsAmount = summary
            ? clampToZero(summary.savingsAmount)
            : null;
        const isZeroSaved = savingsAmount === 0;

        return (
            <ReportMetric
                title={t('spendSummary.totalSpend')}
                primaryTooltip={t(
                    'spendSummary.totalSpendExcludesTaxAndShipping',
                )}
                primary={
                    primaryAmount == null
                        ? '-'
                        : formatReportCurrency(clampToZero(primaryAmount))
                }
                loading={loading}
                minHeight={showSavingsDetail ? '180px' : '100px'}
            >
                {showSavingsDetail ? (
                    <Box
                        sx={detailRowSx}
                        data-testclass="spend-total-detail-row"
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'flex-start',
                                gap: '5px',
                            }}
                        >
                            <span>{t('spendSummary.totalSavings')}</span>
                            <DynamicTooltip
                                content={t(
                                    'savingsSummary.totalSavingsTooltip',
                                )}
                                containerStyle={{
                                    height: '16px',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        fontSize: '1.8rem',
                                        color: '#B7B7B7',
                                    }}
                                    data-pdf-tooltip="true"
                                />
                            </DynamicTooltip>
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                ...detailValueSx,
                                color: isZeroSaved
                                    ? 'rgba(0, 0, 0, 0.50)'
                                    : 'metrics.positive',
                            }}
                        >
                            {savingsAmount == null
                                ? '-'
                                : formatReportCurrency(savingsAmount)}
                        </Box>
                    </Box>
                ) : null}
            </ReportMetric>
        );
    },
);

SpendSummaryTotalSpendMetric.displayName = 'SpendSummaryTotalSpendMetric';
